import React from 'react';
import viewerUserBiometrics from 'models/user/ViewerUserBiometrics';
import { CONSENT_TYPE_AI } from 'services/omt/subscription/subscriptionFields';

export default {
    ENFORCEMENT_TYPE: {
        NONE: 'NONE',
        SOFT: 'SOFT',
        HARD: 'HARD'
    },

    USER_OPTION: {
        OPT_IN: 'opt-in',
        OPT_OUT: 'opt-out'
    },

    CONSENT_TYPE_AI: CONSENT_TYPE_AI,
    CONSENT_TYPE_INSIGHTS: 'DI_CONNECT_INSIGHTS',
    CONSENT_TYPE_UPLOAD: 'DI_CONNECT_UPLOAD',
    CONSENT_TYPE_THIRDPARTY: 'DI_CONNECT_THIRDPARTY',
    CONSENT_TYPE_GARMIN_COACH_ATP: 'DI_CONNECT_ADAPTIVE_TRAINING_PLAN',
    CONSENT_TYPE_CONNECT_PRIVACY: 'DI_CONNECT_CONNECT-PRIVACY',
    CONSENT_TYPE_GOLF_PRIVACY: 'DI_CONNECT_GOLF-PRIVACY',
    CONSENT_TYPE_DI_CONNECT_PRODUCT_IMPROVEMENT_DATA_USAGE: 'DI_CONNECT_PRODUCT_IMPROVEMENT_DATA_USAGE',
    CONSENT_TYPE_DATA_DELETION: 'DI_CONNECT_DATA_DELETION',
    CONSENT_TYPE_DATA_DELETION_CONFIRMATION: 'DI_CONNECT_DATA_DELETION_CONFIRMATION',

    FEATURE_UPLOAD: 'UPLOAD',

    SOURCE_TYPE: {
        DEFAULT: 'DEFAULT',
        CUSTOMER: 'CUSTOMER',
        ALERT: 'ALERT',
        EXPIRED: 'EXPIRED'
    },

    DELETE_STATUS: {
        NOT_APPLICABLE: 'NOT_APPLICABLE',
        IN_PROGRESS: 'IN_PROGRESS',
        NOT_CONFIRMED: 'NOT_CONFIRMED',
        COMPLETED: 'COMPLETED',
        CONFIRMED: 'CONFIRMED',
        LOCATION_CHANGED: 'LOCATION_CHANGED',
        PASSWORD_CHECK_FAILED: 'PASSWORD_CHECK_FAILED'
    },

    DEFAULT_CONSENT_VERSION: '0.0',

    RCD_OPTIONS: {
        KEEP: 'KEEP',
        DELETE: 'DELETE'
    },

    deviceSyncUserPrefenceKey: 'account.deviceSync',

    isFeatureForUserOn: function(userOption, consentEnforcementState, sourceType) {
        // Check consent source type for CN users for CN PIPL
        if (sourceType && viewerUserBiometrics && viewerUserBiometrics.getCountryCode() === 'CN') {
            return (
                (userOption === this.USER_OPTION.OPT_IN && sourceType === this.SOURCE_TYPE.CUSTOMER) ||
                consentEnforcementState === this.ENFORCEMENT_TYPE.NONE
            );
        } else {
            return userOption === this.USER_OPTION.OPT_IN || consentEnforcementState === this.ENFORCEMENT_TYPE.NONE;
        }
    },

    isFeatureExpired: function(sourceType) {
        return sourceType === this.SOURCE_TYPE.EXPIRED;
    },

    isUserUnsubscribed: function(userOption, consentEnforcementState, sourceType) {
        // Check consent source type for CN users for CN PIPL
        if (sourceType && viewerUserBiometrics && viewerUserBiometrics.getCountryCode() === 'CN') {
            return (
                (userOption === this.USER_OPTION.OPT_OUT ||
                    (userOption === this.USER_OPTION.OPT_IN && sourceType === this.SOURCE_TYPE.DEFAULT)) &&
                consentEnforcementState === this.ENFORCEMENT_TYPE.NONE
            );
        }
        return userOption === this.USER_OPTION.OPT_OUT && consentEnforcementState === this.ENFORCEMENT_TYPE.NONE;
    },

    isGDPRDisabled: function(consentEnforcementState) {
        return consentEnforcementState === this.ENFORCEMENT_TYPE.NONE;
    },

    isDataDeletionInProgress: function(deleteStatus) {
        return deleteStatus === this.DELETE_STATUS.IN_PROGRESS || deleteStatus === this.DELETE_STATUS.CONFIRMED;
    },

    /**
     * Formats text fetched from Contentful for use in the GDPR consent modals
     *
     * @param content array of objects that contain copy and style information
     * @param thirdPartyApp string representing the name of the third party app that's requesting consent and replaces the variable '[APP]' that's sent from Contentful
     * @param redEmphasis boolean value for what type of emphasis to use: default is bold, true makes the text red
     */
    getGdprContentText: function(content, thirdPartyApp, redEmphasis) {
        var consentText = '';

        for (let i = 0, length = content.length; i < length; i++) {
            if (content[i].style === 'emphasis' && redEmphasis) {
                consentText +=
                    '<p class="red-emphasis">' +
                    (thirdPartyApp ? content[i].copy.split('[APP]').join(thirdPartyApp) : content[i].copy) +
                    '</p>';
            } else if (content[i].style === 'emphasis') {
                consentText +=
                    '<p class="emphasis">' +
                    (thirdPartyApp ? content[i].copy.split('[APP]').join(thirdPartyApp) : content[i].copy) +
                    '</p>';
            } else {
                consentText +=
                    '<p>' +
                    (thirdPartyApp ? content[i].copy.split('[APP]').join(thirdPartyApp) : content[i].copy) +
                    '</p>';
            }
        }

        return consentText;
    },

    /**
     * React version of the getGdprContentText() method above for use in jsx files
     * Formats text fetched from Contentful for use in the GDPR consent modals
     *
     * @param content array of objects that contain copy and style information
     * @param thirdPartyApp string representing the name of the third party app that's requesting consent and replaces the variable '[APP]' that's sent from Contentful
     * @param redEmphasis boolean value for what type of emphasis to use: default is bold, true makes the text red
     */
    getGdprContentTextForReact: function(content, thirdPartyApp, redEmphasis) {
        let consentText = [];

        for (let i = 0, length = content.length; i < length; i++) {
            if (content[i].style === 'emphasis' && redEmphasis) {
                consentText.push(
                    <p key={i} className={'modalRedEmphasis'}>
                        {thirdPartyApp ? content[i].copy.split('[APP]').join(thirdPartyApp) : content[i].copy}
                    </p>
                );
            } else if (content[i].style === 'emphasis') {
                consentText.push(
                    <p key={i} className={'modalEmphasis'}>
                        {thirdPartyApp ? content[i].copy.split('[APP]').join(thirdPartyApp) : content[i].copy}
                    </p>
                );
            } else {
                consentText.push(
                    <p key={i}>
                        {thirdPartyApp ? content[i].copy.split('[APP]').join(thirdPartyApp) : content[i].copy}
                    </p>
                );
            }
        }

        return consentText;
    },

    shouldAskConsentFor: function(feature) {
        if (feature) {
            const userOption = feature.userOption;
            const consentEnforcementState = feature.consentEnforcementState;

            const isNotConsentEnforcementStateNone = consentEnforcementState !== this.ENFORCEMENT_TYPE.NONE;

            return isNotConsentEnforcementStateNone && !this.isFeatureForUserOn(userOption, consentEnforcementState);
        }
    },

    shouldShowExpiredText: function(feature) {
        if (feature) {
            return this.isFeatureExpired(feature.sourceType);
        }
    },

    consentTypeThirdParty: function(consumerConsentTypeIdSuffix) {
        if (consumerConsentTypeIdSuffix) {
            return `${this.CONSENT_TYPE_THIRDPARTY}_${consumerConsentTypeIdSuffix}`;
        }
        return this.CONSENT_TYPE_THIRDPARTY;
    },

    isUserOptIn: function(gdprFeature) {
        return (
            gdprFeature &&
            this.isFeatureForUserOn(gdprFeature.userOption, gdprFeature.consentEnforcementState, gdprFeature.sourceType)
        );
    }
};
